import { useQuery } from '@tanstack/react-query'
import { Package } from 'lucide-react'
import { type InputHTMLAttributes, useLayoutEffect, useRef, useState } from 'react'
import { api, type Product } from '../lib/api'
import { computeDropdownPlacement, type DropdownPlacement } from '../lib/dropdown-placement'
import { useVisualViewportHeight } from '../lib/use-visual-viewport'
import { cn } from '../lib/utils'

interface ProductAutocompleteProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'onSelect'> {
  value: string
  onChange: (next: string) => void
  // Optional callback fired when the user clicks one of the suggestions —
  // gives the parent a chance to fill in price/unit/etc. besides the
  // description. Defaults to just writing the product name to value.
  onPick?: (product: Product) => void
}

// Filename-style input that shows products whose name or description matches
// the typed text. The dropdown is purely a suggestion — the user can type
// any free-form text and never has to pick one. Products are lazily loaded
// on first focus so we don't query on every row mount.
export function ProductAutocomplete({
  value,
  onChange,
  onPick,
  className,
  ...rest
}: ProductAutocompleteProps) {
  const [focused, setFocused] = useState(false)
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const { data } = useQuery({
    queryKey: ['products', { archived: false }],
    queryFn: () => api.listProducts(false),
    enabled: focused, // lazy: only fetch once the user starts editing
    staleTime: 60_000,
  })
  const products = data?.products ?? []

  const q = value.trim().toLowerCase()
  // Show suggestions only when the user has typed at least 2 chars — keeps
  // a single-char "x" from listing the entire product catalogue.
  const matches =
    q.length >= 2
      ? products
          .filter(
            (p) =>
              p.name.toLowerCase().includes(q) ||
              (p.description?.toLowerCase().includes(q) ?? false),
          )
          .slice(0, 8)
      : []

  const open = focused && matches.length > 0

  // Dropdown-Höhe an den Rest-Viewport unterm Anker koppeln (visualViewport
  // schrumpft bei offener Tastatur); bei < 160px Restplatz über dem Input
  // öffnen. Desktop mit genug Platz bekommt exakt das bisherige
  // `top-full mt-1` + 256px-Cap (max-h-64) — siehe lib/dropdown-placement.ts.
  const viewportHeight = useVisualViewportHeight()
  const [placement, setPlacement] = useState<DropdownPlacement | null>(null)
  useLayoutEffect(() => {
    if (!open) return
    const rect = inputRef.current?.getBoundingClientRect()
    if (!rect) return
    setPlacement(computeDropdownPlacement(rect.top, rect.bottom, viewportHeight))
  }, [open, viewportHeight])
  const openUp = placement?.openUp ?? false

  return (
    <span className="relative inline-block w-full">
      <input
        {...rest}
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={(e) => {
          if (blurTimer.current) clearTimeout(blurTimer.current)
          setFocused(true)
          rest.onFocus?.(e)
        }}
        onBlur={(e) => {
          // Defer the close so a click on a suggestion item registers before
          // the dropdown disappears. 120ms is enough for the mouseup to fire.
          blurTimer.current = setTimeout(() => setFocused(false), 120)
          rest.onBlur?.(e)
        }}
        className={cn('w-full', className)}
        autoComplete="off"
      />
      {open && (
        <div
          role="listbox"
          aria-label="Produkt-Vorschläge"
          className={cn(
            'absolute left-0 right-0 z-30 overflow-y-auto rounded-lg border border-border-default bg-bg-elevated shadow-xl divide-y divide-border-subtle',
            openUp ? 'bottom-full mb-1' : 'top-full mt-1',
          )}
          // Fallback vor der ersten Messung: bisheriger Desktop-Cap (max-h-64).
          style={{ maxHeight: placement?.maxHeight ?? 256 }}
          // Don't let mousedown inside the dropdown blur the parent input —
          // otherwise the click would never reach the suggestion button.
          onMouseDown={(e) => e.preventDefault()}
        >
          {matches.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => {
                if (onPick) onPick(p)
                else onChange(p.name)
                setFocused(false)
              }}
              className="w-full flex items-start gap-2.5 px-3 py-2 text-left text-sm hover:bg-bg-overlay/60 transition-colors"
            >
              <Package className="h-3.5 w-3.5 text-text-tertiary shrink-0 mt-0.5" />
              <span className="flex-1 min-w-0">
                <span className="block truncate font-medium text-text-primary">{p.name}</span>
                {p.description && (
                  <span className="block truncate text-xs text-text-tertiary">{p.description}</span>
                )}
              </span>
              <span className="text-right shrink-0">
                <span className="block font-mono tabular-nums text-accent">
                  {(p.defaultPriceCents / 100).toFixed(2)} €
                </span>
                <span className="block text-2xs text-text-tertiary">/ {p.unit}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </span>
  )
}
